/**
 * Auditoria — Vista global de actividad reciente.
 * Solo admin. Lista los últimos cambios en CO/WO/Q/PO/Piece con
 * createdBy/lastModifiedBy y timestamp.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import AccesosDenegados from "./AccesosDenegados";

const KIND_META = {
  CO:    { label: "OC Cliente",  color: "#3498db", route: "/customerOrders" },
  WO:    { label: "OT Trabajo",  color: "#f39c12", route: "/workOrders"     },
  Q:     { label: "Cotización",  color: "#a78bfa", route: "/quotations"     },
  PO:    { label: "OC Compra",   color: "#16a085", route: "/purchases"      },
  Piece: { label: "Pieza",       color: "#2ecc71", route: "/pieces"         },
};

const fmtDate = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-CL", {
    day: "2-digit", month: "short", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

const Auditoria = () => {
  useTitle("Auditoría");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const authContext = React.useContext(AuthContext);
  const puedeVerAuditoria = authContext?.can?.("ver_auditoria") || false;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filterKind, setFilterKind] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [search, setSearch] = useState("");
  // Dos miradas sobre lo mismo: lo que pasó y lo que no se dejó pasar.
  const [vista, setVista] = useState("actividad");

  useEffect(() => {
    if (!puedeVerAuditoria) return;
    setLoading(true);
    backend.get("auditoria?limit=300")
      .then(({ data }) => setEvents(data.events || []))
      .catch(e => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [puedeVerAuditoria]);

  const usersList = useMemo(() => {
    const set = new Set();
    for (const e of events) {
      if (e.lastModifiedBy) set.add(e.lastModifiedBy);
      if (e.createdBy) set.add(e.createdBy);
    }
    return [...set].sort();
  }, [events]);

  const filtered = useMemo(() => {
    let rows = events;
    if (filterKind !== "all")   rows = rows.filter(e => e.kind === filterKind);
    if (filterAction !== "all") rows = rows.filter(e => e.lastAction === filterAction);
    if (filterUser !== "all") {
      rows = rows.filter(e => e.lastModifiedBy === filterUser || e.createdBy === filterUser);
    }
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter(e => (e.label || "").toLowerCase().includes(q));
    return rows;
  }, [events, filterKind, filterAction, filterUser, search]);

  if (!puedeVerAuditoria) return <Navigate to="/dashboard" replace />;

  const counts = {
    all: events.length,
    CO:    events.filter(e => e.kind === "CO").length,
    WO:    events.filter(e => e.kind === "WO").length,
    Q:     events.filter(e => e.kind === "Q").length,
    PO:    events.filter(e => e.kind === "PO").length,
    Piece: events.filter(e => e.kind === "Piece").length,
  };

  return (
    <div style={{
      padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
      minHeight: "100%", color: "#ddd", fontFamily: "Arial, sans-serif",
      fontSize: 13, boxSizing: "border-box", width: "100%",
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="mv-screen-title" style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Auditoría</div>
          <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 2 }}>
            {vista === "actividad"
              ? "Actividad reciente: quién creó / modificó qué. Datos de los últimos 300 eventos."
              : "Accesos denegados: quién intentó algo que el servidor no dejó pasar."}
          </div>
        </div>
      </div>

      {/* Pestañas. Las dos vistas comparten la pregunta ("¿quién?") y no los datos,
          así que la de accesos vive en su propio componente y trae lo suyo. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <Pestana activa={vista === "actividad"} onClick={() => setVista("actividad")}>Actividad</Pestana>
        <Pestana activa={vista === "accesos"} onClick={() => setVista("accesos")}>Accesos denegados</Pestana>
      </div>

      {vista === "accesos" ? <AccesosDenegados /> : (
      <>

      {/* Cards stat clickables por tipo — en móvil van en carrusel horizontal */}
      <div className="chips-scroll" style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Card label="Total" value={counts.all} color="#3498db"
          onClick={() => setFilterKind("all")} active={filterKind === "all"} />
        {Object.keys(KIND_META).map(k => (
          <Card key={k}
            label={KIND_META[k].label}
            value={counts[k]}
            color={KIND_META[k].color}
            onClick={() => setFilterKind(p => p === k ? "all" : k)}
            active={filterKind === k}
          />
        ))}
      </div>

      {/* C22 — Snapshots de robots (Previred/Enternet); cambioDetectado en rojo */}
      <SnapshotsPanel isAdmin={puedeVerAuditoria} />

      {/* Filtros adicionales */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input
          type="search"
          className="audi-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por OC, OT, descripción…"
          style={{
            background: "#161616", border: "1px solid #333", borderRadius: 5,
            padding: "6px 10px", fontSize: 12, color: "#ddd",
            fontFamily: "Arial,sans-serif", width: 280, boxSizing: "border-box",
          }}
        />
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
          className="audi-select" style={selectStyle}>
          <option value="all">Todos los usuarios</option>
          {usersList.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
          className="audi-select" style={selectStyle}>
          <option value="all">Todas las acciones</option>
          <option value="created">Solo creaciones</option>
          <option value="updated">Solo modificaciones</option>
        </select>
      </div>

      {/* ── Vista móvil: tarjetas tocables en vez de tabla de 7 columnas ──
          Mismos datos (`filtered`), cada tarjeta navega igual que el botón
          "Abrir" de la tabla. Desktop no cambia. */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && <div style={{ padding: 16, textAlign: "center", color: "#aaa", fontSize: 12 }}>Cargando…</div>}
          {error && <div style={{ padding: 16, textAlign: "center", color: "#e74c3c", fontSize: 12 }}>{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div style={{ padding: 16, textAlign: "center", color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
              Sin eventos {filterKind !== "all" || filterUser !== "all" || filterAction !== "all" || search ? "con esos filtros" : ""}
            </div>
          )}
          {filtered.map(e => {
            const meta = KIND_META[e.kind] || { label: e.kind, color: "#cbd5e1", route: "/" };
            return (
              <div
                key={`${e.kind}-${e.id}`}
                className="t-02 tap-card"
                onClick={() => navigate(`${meta.route}?openId=${e.id}`)}
                style={{
                  background: "#232323", border: "1px solid #2e2e2e",
                  borderRadius: 12, padding: "12px 14px", cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                    padding: "2px 8px", borderRadius: 9,
                    background: meta.color + "22", color: meta.color,
                    border: `1px solid ${meta.color}55`,
                  }}>{meta.label}</span>
                  <span style={{
                    flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", color: "#fff", fontFamily: "monospace", fontSize: 12,
                  }}>{e.label}</span>
                  <span style={{ color: "#555", fontSize: 14, lineHeight: 1, flexShrink: 0 }}>›</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: e.lastAction === "created" ? "#2ecc71" : "#3498db",
                  }}>
                    {e.lastAction === "created" ? "✚ Creado" : "✎ Modificado"}
                  </span>
                  {e.status && <span style={{ fontSize: 11, color: "#b8b8b8" }}>· {e.status}</span>}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: "#8a8a8a", fontVariantNumeric: "tabular-nums" }}>{fmtDate(e.updatedAt)}</span>
                </div>
                <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e.lastModifiedBy || e.createdBy || "(legacy / sin usuario)"}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, background: "#242424", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
          <thead>
            <tr style={{ background: "#1a1a1a" }}>
              <Th>Tipo</Th>
              <Th>Identificador</Th>
              <Th>Acción</Th>
              <Th>Estado actual</Th>
              <Th>Modificado por</Th>
              <Th>Última actividad</Th>
              <Th align="right">Ir</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>Cargando…</td></tr>
            )}
            {error && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#e74c3c" }}>{error}</td></tr>
            )}
            {!loading && !error && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#aaa", fontStyle: "italic" }}>
                Sin eventos {filterKind !== "all" || filterUser !== "all" || filterAction !== "all" || search ? "con esos filtros" : ""}
              </td></tr>
            )}
            {filtered.map(e => {
              const meta = KIND_META[e.kind] || { label: e.kind, color: "#cbd5e1", route: "/" };
              return (
                <tr key={`${e.kind}-${e.id}`} style={{ borderTop: "1px solid #1f1f1f" }}>
                  <Td>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      padding: "2px 7px", borderRadius: 9,
                      background: meta.color + "22", color: meta.color,
                      border: `1px solid ${meta.color}55`,
                    }}>{meta.label}</span>
                  </Td>
                  <Td>
                    <span style={{ color: "#fff", fontFamily: "monospace", fontSize: 11 }}>{e.label}</span>
                  </Td>
                  <Td>
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      color: e.lastAction === "created" ? "#2ecc71" : "#3498db",
                    }}>
                      {e.lastAction === "created" ? "✚ Creado" : "✎ Modificado"}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 10, color: "#cbd5e1" }}>{e.status || "—"}</span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 11, color: "#aaa" }}>
                      {e.lastModifiedBy || e.createdBy || <i style={{ color: "#999" }}>(legacy / sin usuario)</i>}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 11, color: "#cbd5e1" }}>{fmtDate(e.updatedAt)}</span>
                  </Td>
                  <Td align="right">
                    <button
                      onClick={() => navigate(`${meta.route}?openId=${e.id}`)}
                      style={{
                        background: "#3498db22", color: "#3498db",
                        border: "1px solid #3498db55", borderRadius: 4,
                        padding: "3px 10px", fontSize: 10, fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >→ Abrir</button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
        {filtered.length} de {events.length} eventos
        {filtered.length === events.length ? "" : " (filtrado)"}
      </div>

      </>
      )}
    </div>
  );
};

const Pestana = ({ activa, onClick, children }) => (
  <button onClick={onClick} className="t-02" style={{
    background: activa ? "#3498db22" : "transparent",
    color: activa ? "#3498db" : "#aaa",
    border: `1px solid ${activa ? "#3498db55" : "#2e2e2e"}`,
    borderRadius: 6, padding: "6px 14px", fontSize: 12,
    fontWeight: activa ? 700 : 500, cursor: "pointer",
    fontFamily: "Arial, sans-serif",
  }}>{children}</button>
);

const Card = ({ label, value, color, onClick, active }) => (
  <div onClick={onClick} className="audi-stat tap-card" style={{
    flex: "1 1 0", minWidth: 110, padding: "10px 14px",
    background: active ? `${color}22` : "#242424",
    border: `1px solid ${active ? color : "#2e2e2e"}`,
    borderRadius: 6, cursor: "pointer",
  }}>
    <div style={{ fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
  </div>
);

const Th = ({ children, align = "left" }) => (
  <th style={{
    padding: "8px 12px", textAlign: align, fontSize: 9, fontWeight: 700,
    color: "#aaa", textTransform: "uppercase", letterSpacing: ".06em",
    borderBottom: "1px solid #2e2e2e", whiteSpace: "nowrap",
  }}>{children}</th>
);

const Td = ({ children, align = "left" }) => (
  <td style={{ padding: "8px 12px", textAlign: align, fontSize: 12, color: "#bbb", verticalAlign: "middle" }}>{children}</td>
);

// C22 — Panel de snapshots de los robots de auditoría (Previred, Enternet).
// Autocontenido: hace su propio fetch a /auditoria/snapshots. cambioDetectado = rojo.
const SnapshotsPanel = ({ isAdmin }) => {
  const [snap, setSnap] = useState({ previred: [], enternet: [] });
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    backend.get("auditoria/snapshots")
      .then(({ data }) => setSnap({ previred: data.previred || [], enternet: data.enternet || [] }))
      .catch(e => setErr(e?.response?.data?.error || e.message));
  }, [isAdmin]);

  const fmtFecha = d => (d ? new Date(d).toLocaleDateString("es-CL") : "—");
  const Lista = ({ titulo, filas }) => (
    <div style={{ flex: "1 1 320px", minWidth: 300, background: "#242424", border: "1px solid #2e2e2e", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".05em" }}>{titulo}</div>
      {filas.length === 0 ? (
        <div style={{ fontSize: 12, color: "#777" }}>Sin snapshots.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <Th>Período</Th><Th>Tomado</Th><Th align="right">Archivos</Th><Th align="right">Rev.</Th><Th>Estado</Th>
          </tr></thead>
          <tbody>
            {filas.map(s => (
              <tr key={s.periodo} style={s.cambioDetectado ? { background: "#e74c3c1a" } : undefined}>
                <Td>{s.periodo}</Td>
                <Td>{fmtFecha(s.fecha)}</Td>
                <Td align="right">{s.nArchivos}</Td>
                <Td align="right">{s.revisiones}</Td>
                <Td>
                  {s.cambioDetectado
                    ? <span style={{ color: "#e74c3c", fontWeight: 700 }}>⚠ cambió ({s.nHistorial})</span>
                    : <span style={{ color: "#2ecc71" }}>ok</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  if (!isAdmin) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 6 }}>
        Robots de auditoría — huella de los archivos por período.{" "}
        <span style={{ color: "#e74c3c" }}>Rojo</span> = el conjunto cambió después de registrado (rectificación).
      </div>
      {err && <div style={{ fontSize: 12, color: "#e74c3c", marginBottom: 6 }}>Error: {err}</div>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Lista titulo="Previred" filas={snap.previred} />
        <Lista titulo="Enternet (DTE)" filas={snap.enternet} />
      </div>
    </div>
  );
};

const selectStyle = {
  background: "#161616", border: "1px solid #333", borderRadius: 5,
  padding: "6px 10px", fontSize: 12, color: "#ddd",
  fontFamily: "Arial,sans-serif", cursor: "pointer",
};

export default Auditoria;
